import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  ListItemButton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Divider,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  PhotoCamera as PhotoCameraIcon,
  ChangeCircle as ChangePasswordIcon,
  Delete as DeleteIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Login as LoginIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Security as TwoFAIcon,
  Devices as DevicesIcon,
  Logout as LogoutIcon,
  PersonRemove as DeleteAccountIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { formatForDisplay as formatDateWithTimezone } from '@utils/dateFormatter';
import { useNavigate } from 'react-router-dom';
import { avatarService } from '@services/avatarService';
import { useAvatar } from '@contexts/AvatarContext';
import { authService } from '@services/authService';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const { avatarUrl, updateAvatar } = useAvatar();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [notificationSettings, setNotificationSettings] = useState({
    email: true,
    push: true,
    sms: false,
  });
  const [accountSettings, setAccountSettings] = useState({
    twoFA: false,
    activeSessions: 3,
    lastLogin: '2025-10-10T19:40:00Z', // 22:40 ora Romaniei (UTC+3)
    profileCompletion: 85,
    accountStatus: 'active' as 'active' | 'suspended' | 'pending',
  });
  const [recentActivity, setRecentActivity] = useState([
    { id: 1, action: 'Ai schimbat parola', date: '2025-10-10T14:30:00Z' }, // 17:30 ora Romaniei
    { id: 2, action: 'Ai actualizat adresa', date: '2025-10-10T16:45:00Z' }, // 19:45 ora Romaniei
    { id: 3, action: 'Ai incarcat o poza de profil', date: '2025-10-10T19:35:00Z' }, // 22:35 ora Romaniei (acum 10 min)
  ]);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: '+40 123 456 789',
    address: 'Strada Exemplului, Nr. 123, Bucuresti',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = () => {
    // Aici ar fi logica pentru salvarea datelor
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: '+40 123 456 789',
      address: 'Strada Exemplului, Nr. 123, Bucuresti',
    });
    setIsEditing(false);
  };

  // Functii pentru actiunile rapide
  const handleProfileImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      
      // Validare fisier
      if (!file.type.startsWith('image/')) {
        setNotification({
          open: true,
          message: 'Te rugam sa selectezi o imagine valida.',
          severity: 'error'
        });
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB
        setNotification({
          open: true,
          message: 'Imaginea este prea mare. Marimea maxima este 5MB.',
          severity: 'error'
        });
        return;
      }

      setIsUploadingAvatar(true);

      try {
        // Creeaza URL pentru preview imediat
        const reader = new FileReader();
        reader.onload = (event) => {
          const imageData = event.target?.result as string;
          updateAvatar(imageData); // Actualizeaza avatar-ul global
        };
        reader.readAsDataURL(file);

        // Incearca upload la backend
        const response = await avatarService.uploadAvatar(file);
        
        if (response.success) {
          setNotification({
            open: true,
            message: response.message || 'Avatar-ul a fost incarcat cu succes!',
            severity: 'success'
          });
          
          // Actualizeaza cu URL-ul de la backend daca este disponibil
          if (response.avatarUrl) {
            updateAvatar(response.avatarUrl);
          }
        } else {
          throw new Error(response.message);
        }
      } catch (error: any) {
        console.error('Avatar upload error:', error);
        setNotification({
          open: true,
          message: error.message || 'Eroare la incarcarea avatar-ului.',
          severity: 'error'
        });
        
        // Revert preview doar daca nu este o eroare de backend (foloseste fallback local)
        if (!error.message?.includes('backend indisponibil')) {
          updateAvatar(null);
        }
      } finally {
        setIsUploadingAvatar(false);
      }
    }
  };

  const handleChangePassword = () => {
    setChangePasswordDialog(true);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handlePasswordSubmit = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setNotification({
        open: true,
        message: 'Parolele nu se potrivesc.',
        severity: 'error'
      });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setNotification({
        open: true,
        message: 'Parola noua trebuie sa aiba cel putin 8 caractere.',
        severity: 'error'
      });
      return;
    }

    // Validare pattern pentru parola noua (consistent cu backend-ul)
    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!passwordPattern.test(passwordData.newPassword)) {
      setNotification({
        open: true,
        message: 'Parola noua trebuie sa contina cel putin o litera mica, o litera mare, o cifra si un caracter special.',
        severity: 'error'
      });
      return;
    }

    try {
      // Apeleaza API-ul pentru schimbarea parolei
      console.log('🔍 Trimitere request schimbare parola...', {
        currentPassword: '***',
        newPassword: '***',
        newPasswordLength: passwordData.newPassword.length
      });
      
      const result = await authService.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );

      console.log('📥 Raspuns de la server:', result);

      if (result.success) {
        setNotification({
          open: true,
          message: result.message || 'Parola a fost schimbata cu succes!',
          severity: 'success'
        });
        setChangePasswordDialog(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        console.error('❌ Eroare la schimbarea parolei:', result.error);
        setNotification({
          open: true,
          message: result.error || 'Eroare la schimbarea parolei. Incearca din nou.',
          severity: 'error'
        });
      }
    } catch (error: any) {
      console.error('💥 Exceptie la schimbarea parolei:', error);
      setNotification({
        open: true,
        message: 'Eroare la schimbarea parolei. Incearca din nou.',
        severity: 'error'
      });
    }
  };

  const handleNotificationPreferences = () => {
    navigate('/settings?tab=notifications');
  };

  const handleAccountSettings = () => {
    navigate('/settings');
  };

  const handleNotificationToggle = (type: keyof typeof notificationSettings) => {
    setNotificationSettings(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
    
    setNotification({
      open: true,
      message: `Notificarile ${type} au fost ${notificationSettings[type] ? 'dezactivate' : 'activate'}`,
      severity: 'success'
    });
  };

  const handleTwoFAToggle = () => {
    setAccountSettings(prev => ({
      ...prev,
      twoFA: !prev.twoFA
    }));
    
    setNotification({
      open: true,
      message: `Autentificarea in doi pasi a fost ${accountSettings.twoFA ? 'dezactivata' : 'activata'}`,
      severity: 'success'
    });
  };

  const handleLogoutAllSessions = () => {
    setNotification({
      open: true,
      message: 'Toate sesiunile au fost inchise cu succes!',
      severity: 'success'
    });
    setAccountSettings(prev => ({
      ...prev,
      activeSessions: 1
    }));
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Esti sigur ca vrei sa stergi contul? Aceasta actiune nu poate fi anulata.')) {
      setNotification({
        open: true,
        message: 'Contul va fi sters in 30 de zile.',
        severity: 'info'
      });
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      setIsUploadingAvatar(true);
      const response = await avatarService.deleteAvatar();
      
      updateAvatar(null);
      
      setNotification({
        open: true,
        message: response.message || 'Avatar-ul a fost sters cu succes!',
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Avatar delete error:', error);
      
      // Daca backend-ul nu este disponibil, sterge local
      if (error.message?.includes('backend indisponibil')) {
        updateAvatar(null);
        setNotification({
          open: true,
          message: 'Avatar-ul a fost sters local!',
          severity: 'success'
        });
      } else {
        setNotification({
          open: true,
          message: error.message || 'Eroare la stergerea avatar-ului.',
          severity: 'error'
        });
      }
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Avatar-ul se incarca automat prin AvatarContext

  const getRoleText = (role: string) => {
    switch (role) {
      case 'client':
        return 'Client';
      case 'veterinarian':
        return 'Veterinar';
      case 'clinic_admin':
        return 'Administrator Clinica';
      case 'master_admin':
        return 'Administrator Principal';
      default:
        return 'Utilizator';
    }
  };

  const formatDate = (dateString: string) => {
    // Foloseste utilitarul cu timezone corect pentru Romania
    return formatDateWithTimezone(dateString);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Activ';
      case 'suspended':
        return 'Suspendat';
      case 'pending':
        return 'In asteptare';
      default:
        return 'Necunoscut';
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Profil
      </Typography>

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
        gap: 4 
      }}>
        {/* Informatii Personale */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                Informatii Personale
              </Typography>
              <Button
                variant="outlined"
                startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
                onClick={isEditing ? handleSave : () => setIsEditing(true)}
                size="small"
                sx={{ borderRadius: 2 }}
              >
                {isEditing ? 'Salveaza' : 'Editeaza'}
              </Button>
            </Box>

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 3 
            }}>
              <TextField
                fullWidth
                label="Prenume"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                disabled={!isEditing}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                label="Nume"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                disabled={!isEditing}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                disabled={!isEditing}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                label="Telefon"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={!isEditing}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                label="Adresa"
                name="address"
                value={formData.address}
                onChange={handleChange}
                disabled={!isEditing}
                multiline
                rows={2}
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  gridColumn: { xs: '1', sm: '1 / -1' }
                }}
              />
            </Box>

            {isEditing && (
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button variant="outlined" onClick={handleCancel} startIcon={<CancelIcon />} sx={{ borderRadius: 2 }}>
                  Anuleaza
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Informatii Profesionale - doar pentru roluri non-client */}
        {user?.role && user.role !== 'client' && (
          <Card sx={{ borderRadius: 3, mt: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 3 }}>
                Informatii Profesionale
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 3 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <WorkIcon sx={{ color: 'grey.600' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Functie
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {getRoleText(user.role)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <BusinessIcon sx={{ color: 'grey.600' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Clinica
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {user.clinicName || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <CalendarIcon sx={{ color: 'grey.600' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Membru din
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDate('2024-01-15T00:00:00Z')}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <LoginIcon sx={{ color: 'grey.600' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Ultima logare
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDate(accountSettings.lastLogin)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Profil si Statistici */}
        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Box 
                  sx={{ 
                    position: 'relative',
                    '&:hover .avatar-overlay': {
                      opacity: 1
                    }
                  }}
                >
              <Avatar
                    src={avatarUrl || undefined}
                sx={{
                      width: 96,
                      height: 96,
                  bgcolor: 'primary.main',
                      fontSize: '2.5rem',
                      border: '4px solid',
                      borderColor: 'grey.200',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'scale(1.05)'
                      }
                    }}
                    onClick={handleProfileImageClick}
                  >
                    {!avatarUrl && (user?.firstName?.charAt(0) || 'U')}
              </Avatar>
                  
                  {/* Hover Overlay */}
                  <Box
                    className="avatar-overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      opacity: 0,
                      transition: 'opacity 0.2s ease-in-out',
                      cursor: 'pointer'
                    }}
                    onClick={handleProfileImageClick}
                  >
                    <Tooltip title="Schimba poza">
                      <IconButton
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          width: 36,
                          height: 36,
                        }}
                        disabled={isUploadingAvatar}
                      >
                        <PhotoCameraIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    {avatarUrl && (
                      <Tooltip title="Sterge poza">
                        <IconButton
                          sx={{
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'error.dark' },
                            width: 36,
                            height: 36,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAvatar();
                          }}
                          disabled={isUploadingAvatar}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem', mt: 2 }}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Chip
                label={getRoleText(user?.role || 'client')}
                color="primary"
                variant="outlined"
                sx={{ mt: 1, borderRadius: 2 }}
              />
              
              {/* Status cont */}
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={getStatusText(accountSettings.accountStatus)}
                  color={getStatusColor(accountSettings.accountStatus) as any}
                  size="small"
                  variant="filled"
                  sx={{ borderRadius: 2 }}
                />
              </Box>

              {/* Progress bar completare profil */}
              <Box sx={{ width: '100%', mt: 3, px: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Completare profil
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {accountSettings.profileCompletion}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={accountSettings.profileCompletion} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4
                    }
                  }} 
                />
              </Box>
            </Box>

            <List dense>
              <ListItem>
                <ListItemIcon>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Email"
                  secondary={user?.email}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <PhoneIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Telefon"
                  secondary="+40 123 456 789"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <LocationIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Adresa"
                  secondary="Bucuresti, Romania"
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Actiuni Rapide */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 3 }}>
              Actiuni Rapide
            </Typography>
            
            {/* Schimba parola */}
            <ListItemButton 
              onClick={handleChangePassword}
              sx={{ borderRadius: 2, mb: 1, px: 2 }}
            >
                <ListItemIcon>
                <ChangePasswordIcon />
                </ListItemIcon>
                <ListItemText primary="Schimba parola" />
              </ListItemButton>

            <Divider sx={{ my: 2 }} />

            {/* Preferinte notificari */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
              Notificari
            </Typography>
            
            <Box sx={{ px: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.email}
                    onChange={() => handleNotificationToggle('email')}
                    color="primary"
                  />
                }
                label="Email"
              />
            </Box>
            
            <Box sx={{ px: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.push}
                    onChange={() => handleNotificationToggle('push')}
                    color="primary"
                  />
                }
                label="Push"
              />
            </Box>
            
            <Box sx={{ px: 2, mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.sms}
                    onChange={() => handleNotificationToggle('sms')}
                    color="primary"
                  />
                }
                label="SMS"
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Setari cont avansate */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
              Securitate
            </Typography>
            
            <Box sx={{ px: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={accountSettings.twoFA}
                    onChange={handleTwoFAToggle}
                    color="primary"
                  />
                }
                label="Autentificare in doi pasi"
              />
            </Box>
            
            <ListItemButton 
              onClick={handleLogoutAllSessions}
              sx={{ borderRadius: 2, mb: 1, px: 2 }}
            >
                <ListItemIcon>
                <LogoutIcon />
                </ListItemIcon>
              <ListItemText 
                primary="Inchide toate sesiunile" 
                secondary={`${accountSettings.activeSessions} sesiuni active`}
              />
              </ListItemButton>
            
            <ListItemButton 
              onClick={handleDeleteAccount}
              sx={{ borderRadius: 2, mb: 1, px: 2, color: 'error.main' }}
            >
                <ListItemIcon>
                <DeleteAccountIcon sx={{ color: 'error.main' }} />
                </ListItemIcon>
              <ListItemText primary="Sterge cont" />
              </ListItemButton>
          </CardContent>
        </Card>

        {/* Activitate recenta */}
        <Card sx={{ borderRadius: 3, mt: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 3 }}>
              Activitate recenta
            </Typography>
            <List dense>
              {recentActivity.map((activity, index) => (
                <React.Fragment key={activity.id}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <HistoryIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={activity.action}
                      secondary={formatDate(activity.date)}
                    />
                  </ListItem>
                  {index < recentActivity.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>

      {/* Input pentru fisier ascuns */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageUpload}
        accept="image/*"
        style={{ display: 'none' }}
      />

      {/* Dialog pentru schimbarea parolei */}
      <Dialog open={changePasswordDialog} onClose={() => setChangePasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schimba parola</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Parola curenta"
              type={showPasswords.current ? "text" : "password"}
              name="currentPassword"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              margin="normal"
              required
              helperText="Click pe ochi pentru a vedea parola"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility('current')}
                      edge="end"
                    >
                      {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Parola noua"
              type={showPasswords.new ? "text" : "password"}
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              margin="normal"
              required
              helperText="Minim 8 caractere, cu litera mare, mică, cifră și caracter special"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility('new')}
                      edge="end"
                    >
                      {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirma parola noua"
              type={showPasswords.confirm ? "text" : "password"}
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility('confirm')}
                      edge="end"
                    >
                      {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordDialog(false)}>
            Anuleaza
          </Button>
          <Button onClick={handlePasswordSubmit} variant="contained">
            Schimba parola
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pentru notificari */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile; 
